import { CircularProgress } from "@mui/material";
import { HitDetailFooter } from "components/Footer/HitDetailFooter";
import { flyoutFooter } from "components/HitDetailFlyout/HitDetailFlyout";
import { HitInformationTile, SecondaryInformationTiles } from "components/Tile";
import { HitInformationTileProps } from "components/Tile/HitInformationTile";
import { RootState } from "MyTypes";
import React from "react";
import { Messages } from "components/Tile/Tiles.Messages";
import { useSelector } from "react-redux";
import { getIsFetchingFlyoutEntityDetail, getCurrentUser } from "state/selectors";
import { HitResult } from "./HitResult";
import { HitsCardData } from "./HitCardsPage";
import AddIcon from "@mui/icons-material/Add";
import CommentCard from "components/Cards/CommentCard";
import { HitStatus, SearchVersion, QuickSearch } from "aderant-conflicts-models";
import { conflictsPalette } from "styles/conflictsPalette";

// Constants
const FOOTER_HEIGHT = "120px";
const FOOTER_PADDING = "14px 28px";
const CONTENT_GAP = "12px";

const contentContainerStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: CONTENT_GAP
};

interface UserChangePermissions {
    hitStatus: boolean;
    hitOwner: boolean;
    noChangesAllowed: boolean;
}

interface HitDetailMobilePageProps {
    data: HitResult | null;
    hit: HitsCardData | null;
    entityId: string | null;
    searchVersion: SearchVersion | QuickSearch;
    userChangePermissions: UserChangePermissions;
    searchReadOnly?: boolean;
    onHitChange?: (status?: HitStatus, hitOwnerId?: string | null) => void;
    toggleRequestTermsDialog: () => void;
    handleCommentClick?: (hit: HitsCardData) => void;
    hasReviewerRole?: boolean;
}

export const addCommentStyles = {
    addCommentContainer: {
        display: "flex",
        height: "44px",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
        // original was just color value; keeping same structure while replacing with palette color
        border: conflictsPalette.primary.R2D2,
        padding: "16px",
        backgroundColor: conflictsPalette.background.white,
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        cursor: "pointer"
    },
    addCommentText: {
        color: conflictsPalette.primary.R2D2,
        fontWeight: 600,
        fontSize: "14px"
    },
    addCommentIcon: {
        color: conflictsPalette.primary.R2D2,
        fontSize: "18px"
    }
};

const HitDetailMobilePage = (props: HitDetailMobilePageProps): JSX.Element => {
    const { data, hit, entityId, searchVersion, userChangePermissions, searchReadOnly = false, onHitChange, toggleRequestTermsDialog, handleCommentClick, hasReviewerRole } = props;

    const isFetchingEntity = useSelector((rootState: RootState) => (entityId ? getIsFetchingFlyoutEntityDetail(rootState, entityId) : false));
    const currentUser = useSelector(getCurrentUser);

    const hitInformationTileProps: Partial<HitInformationTileProps> = searchVersion?.isQuickSearch
        ? {
              searchVersion: searchVersion,
              searchReadOnly: true
          }
        : {
              searchVersion: searchVersion,
              searchReadOnly: searchReadOnly,
              onHitChange: onHitChange
          };

    const secondaryTileData = useSelector((state: RootState) => state.search.hitFlyoutDetail);

    if (!data) {
        return (
            <div style={{ padding: "2rem", textAlign: "center" }}>
                <div style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>No hit data available</div>
                <div style={{ fontSize: "0.875rem", color: conflictsPalette.text.grey }}>Please select a hit to view details</div>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <div style={{ ...contentContainerStyle, paddingBottom: FOOTER_HEIGHT }}>
                <div>
                    {hit?.hitCommentCount ? (
                        <div onClick={() => handleCommentClick?.(hit)}>
                            <CommentCard 
                                name={hit?.commentedBy} 
                                time={hit?.latestCommentedOn} 
                                message={hit?.latestCommentText} 
                                count={hit?.hitCommentCount} 
                                isCurrentUser={hit?.commentedByUserId === currentUser?.id}
                            />
                        </div>
                    ) : (
                        hit && handleCommentClick && (
                            <div style={addCommentStyles.addCommentContainer} onClick={() => handleCommentClick(hit)}>
                                <span style={addCommentStyles.addCommentText}>Add a comment</span>
                                <AddIcon style={addCommentStyles.addCommentIcon} />
                            </div>
                        )
                    )}
                </div>

                <HitInformationTile data={data} userChangePermissions={userChangePermissions} {...hitInformationTileProps} />
                {data?.isRedacted ? (
                    <div style={{ padding: "1rem", textAlign: "center", color: conflictsPalette.text.grey }}>This hit information is redacted</div>
                ) : isFetchingEntity ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                        <CircularProgress variant="indeterminate" color="primary" size="2rem" title={Messages.LOADING_DATA_PLEASE_WAIT_TITLE.getMessage()} />
                    </div>
                ) : secondaryTileData.entity && secondaryTileData?.entity?.fields?.length > 0 ? (
                    <SecondaryInformationTiles entityDetails={secondaryTileData.entity} toggleRequestTermsDialog={toggleRequestTermsDialog} />
                ) : (
                    <div role="status" style={{ fontSize: "1rem", textAlign: "center", padding: "1rem" }}>
                        {Messages.NO_DATA_AVAILABLE_MESSAGE.getMessage()}
                    </div>
                )}
            </div>

            <div style={{
                ...flyoutFooter,
                position: "fixed",
                left: 0,
                right: 0,
                marginLeft: "calc(-50vw + 50%)",
                padding: FOOTER_PADDING
            }}>
                <HitDetailFooter data={data} searchVersion={searchVersion} searchReadOnly={searchReadOnly} userChangePermissions={userChangePermissions} onHitChange={onHitChange} hasReviewerRole={hasReviewerRole} />
            </div>
        </div>
    );
};

export default HitDetailMobilePage;
